import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import isPreRendering from 'utils/isPreRendering'
import { isMobile } from 'utils/deviceDetector'
import initialFilters from 'lib/filters'

import {
  SelectInput,
  IdSearchField,
  Title,
  Toggle,
  ValueFilter,
  Localities,
  FiltersCategory,
} from 'components'
import {
  campaignConfigurations,
  hasFirstActiveCampaign,
  hasSecondActiveCampaign,
  replaceCampaignToShow,
} from 'utils/checkCampaign'

import isIos from 'utils/isIos'
import styles from './Filter.module.scss'

const Filter = ({
  filter,
  blocked,
  className,
  setClickedBy,
  type,
  clearFilters,
  hideSearchById,
  setField,
  toggleFilter,
  setSelectedLocalities,
  removeSelectedLocality,
  specificFilters,
  setFieldManually,
  toggleUniqueCheckbox,
  titleContainer,
  setMaxValueRange,
  setMinValueRange,
  setCampaign,
  setCurrentBanner,
}) => {
  const {
    searchId,
    valueRange,
    visits,
    noLegalActions,
    noDebts,
    filters,
    localities,
    selectedLocalities,
    surroundingsSearch,
    campaigns,
  } = filter

  const FilterTitle = props => <Title type="h3" font="small" {...props} />

  const {
    min: minAllowed,
    max: maxAllowed,
    step,
  } = initialFilters.initialValueRange

  const storeId = useRef(searchId)

  const [idQuery, setIdQuery] = useState({
    searchId: storeId.current,
    force: null,
  })

  useEffect(() => {
    storeId.current = searchId
    setIdQuery({ searchId, force: null })
  }, [searchId])

  useEffect(() => {
    const { force, searchId: localId } = idQuery

    if (localId && localId !== searchId && (localId.length >= 5 || force)) {
      setField('searchId', localId)
      setIdQuery({ searchId: localId, force: null })
    }
    // eslint-disable-next-line
  }, [idQuery.searchId, idQuery.force, setField, idQuery])

  const toggleFieldManually = (field, value) => {
    toggleFilter(field, value)
    setField('presetProfile', false)
    setClickedBy('')
  }

  const renderToggleFilter = (filterName, filterOptions) => {
    if (!Array.isArray(filterOptions)) {
      return null
    }

    const singleCheckBoxFilters = ['Desagio']
    const forcedSingleRowFilters = ['Desagio', 'Tipo de Imóvel']

    const forceSingleRow = forcedSingleRowFilters.includes(filterName)

    const opts = filterOptions.map(opt => ({
      id: className + filterName + opt.value,
      name: opt.name,
      checked: opt.checked,
      onChange: () =>
        singleCheckBoxFilters.includes(filterName)
          ? toggleUniqueCheckbox(filterName, opt.value)
          : toggleFieldManually(filterName, opt.value),
    }))

    return <FiltersCategory options={opts} forceSingleRow={forceSingleRow} />
  }

  const filterComponents = [
    {
      key: 'locality',
      title: 'Localidade',
      component: (
        <SelectInput
          tabIndex="0"
          invertIconOnX
          setSlug={newSlug => setField('slug', newSlug)}
          availableLocalities={localities}
          aria-autocomplete="list"
          setSelectedLocalities={setSelectedLocalities}
          selectedLocalities={selectedLocalities}
          autoComplete="off"
          aria-haspopup="true"
          role="combobox"
          className={styles.searchInput}
          type={type}
          onFocus={isIos() ? window.scrollTo(0, 0) : () => {}}
        />
      ),
    },
    {
      key: 'selectedLocalities',
      title: selectedLocalities.length ? 'Localidades selecionadas' : '',
      component: selectedLocalities && selectedLocalities.length && (
        <Localities
          localities={selectedLocalities}
          removeLocality={removeSelectedLocality}
          carrouselMode={isMobile}
        />
      ),
    },
    {
      key: 'surroundingsSearch',
      component: (
        <Toggle
          checked={surroundingsSearch}
          onChange={value => setFieldManually('surroundingsSearch', value)}
          option="Buscar na região"
          optionPosition="right-aligned"
        />
      ),
    },
    {
      key: 'purpose',
      title: 'Finalidade',
      remoteProp: 'Finalidade',
    },
    {
      key: 'propertyType',
      title: 'Tipo de imóvel',
      remoteProp: 'Tipo de Imóvel',
    },
    {
      key: 'value',
      title: 'Valor',
      component: (
        <ValueFilter
          rangeSlider={{
            min: minAllowed,
            max: maxAllowed,
            onChange: value => setField('valueRange', value),
            step,
            valueRange,
          }}
          rangeValueInputMin={{
            id: `min-value-${type}`,
            valueRangeObject: valueRange,
            onBlur: setMinValueRange(valueRange),
            valueRange: valueRange.min,
            min: minAllowed,
            max: valueRange.max,
            step,
          }}
          rangeValueInputMax={{
            id: `max-value-${type}`,
            valueRangeObject: valueRange,
            onBlur: setMaxValueRange(valueRange),
            valueRange: valueRange.max,
            min: valueRange.min,
            max: maxAllowed,
            step,
          }}
        />
      ),
    },
    {
      key: 'saleCategory',
      title: 'Categoria de venda',
      remoteProp: 'Categoria de Venda',
    },
    {
      key: 'listingType',
      title: 'Tipo de venda',
      remoteProp: 'Tipo de Venda',
    },
    {
      key: 'status',
      title: 'Situação',
      remoteProp: 'Situação',
    },
    {
      key: 'purchaseType',
      title: 'Tipo de compra',
      remoteProp: 'Tipo de Compra',
    },
    {
      key: 'legalDispute',
      title: 'Ações judiciais',
      component: (
        <Toggle
          checked={noLegalActions}
          onChange={value => setFieldManually('noLegalActions', value)}
          option="Sem ações judiciais"
        />
      ),
    },
    {
      key: 'debts',
      title: 'Dívidas',
      component: (
        <Toggle
          checked={noDebts}
          onChange={value => setFieldManually('noDebts', value)}
          option="Imóvel sem dívidas"
        />
      ),
    },
    {
      key: 'visits',
      title: 'Visitas',
      component: (
        <Toggle
          checked={visits}
          onChange={value => setFieldManually('visits', value)}
          option="Permite visitar"
        />
      ),
    },
    {
      key: 'discount',
      title: 'Deságio',
      remoteProp: 'Desagio',
    },
    // {
    //   key: 'paymentMethod',
    //   title: 'Formas de pagamento',
    //   remoteProp: 'Formas de pagamento',
    // },
  ]

  const hasFirstCampaignFilter =
    hasFirstActiveCampaign() &&
    campaignConfigurations(process.env.REACT_APP_CAMPAIGN) &&
    campaignConfigurations(process.env.REACT_APP_CAMPAIGN).filter

  const hasSecondCampaignFilter =
    hasSecondActiveCampaign() &&
    campaignConfigurations(process.env.REACT_APP_SECOND_CAMPAIGN) &&
    campaignConfigurations(process.env.REACT_APP_SECOND_CAMPAIGN).filter

  const onChangeCampaign = (campaign, value, campaignName) => {
    setCampaign(campaign, value)
    setCurrentBanner(filter.currentBannerList, value, campaignName)
  }

  if (hasFirstCampaignFilter) {
    filterComponents.splice(3, 0, {
      key: 'campaign',
      title: 'Campanhas',
      component: (
        <Toggle
          checked={campaigns && campaigns.campaign}
          onChange={value =>
            onChangeCampaign('campaign', value, process.env.REACT_APP_CAMPAIGN)
          }
          option={replaceCampaignToShow(process.env.REACT_APP_CAMPAIGN)}
          optionPosition="right-aligned"
          id="campaign-toggle"
        />
      ),
    })
  }

  if (hasSecondCampaignFilter) {
    filterComponents.splice(hasFirstCampaignFilter ? 4 : 3, 0, {
      key: 'secondCampaign',
      title: hasFirstCampaignFilter ? false : 'Campanhas',
      component: (
        <Toggle
          checked={campaigns && campaigns.secondCampaign}
          onChange={value =>
            onChangeCampaign(
              'secondCampaign',
              value,
              process.env.REACT_APP_SECOND_CAMPAIGN
            )
          }
          option={replaceCampaignToShow(process.env.REACT_APP_SECOND_CAMPAIGN)}
          optionPosition="right-aligned"
          id="second-campaign-toggle"
        />
      ),
    })
  }

  if (!hideSearchById) {
    filterComponents.push({
      key: 'searchById',
      title: 'Buscar por ID',
      component: (
        <IdSearchField
          onChange={value => setIdQuery({ ...idQuery, searchId: value })}
          value={idQuery.searchId}
          handleSearch={() =>
            setIdQuery({ searchId: idQuery.searchId, force: true })
          }
          id={`find-by-id-${type}`}
          setIdQuery={setIdQuery}
          setField={setField}
          idQuery={idQuery}
        />
      ),
      className: styles.idSearchField,
    })
  }

  let filteredComponents = []
  if (specificFilters.length) {
    filteredComponents = filterComponents
      .filter(component => specificFilters.includes(component.key))
      .sort((a, b) => {
        return specificFilters.indexOf(a.key) - specificFilters.indexOf(b.key)
      })
  }

  const renderFilterComponents = filteredComponents.length
    ? filteredComponents
    : filterComponents

  const shouldNotRenderHr = specificFilters.length
    ? [
        'locality',
        'selectedLocalities',
        'searchById',
        'surroundingsSearch',
        'propertyType',
        'secondCampaign',
      ]
    : [
        'selectedLocalities',
        'searchById',
        'surroundingsSearch',
        hasFirstActiveCampaign() && 'secondCampaign',
      ]

  return (
    !isPreRendering() && (
      <form
        autoComplete="false"
        className={classNames(styles.form, className, {
          [styles.filterOpacity]: blocked,
        })}
        key={className}
      >
        {type !== 'modal' && (
          <div className={styles.header}>
            <Title type="h2" fontSize="medium" color="primaryColor">
              {titleContainer || 'Refinar minha busca'}
            </Title>
            <button
              type="button"
              onClick={() => {
                setIdQuery({
                  searchId: '',
                })
                clearFilters()
              }}
            >
              Limpar filtros
            </button>
          </div>
        )}
        {renderFilterComponents.map(
          (
            { title, component, remoteProp, values, hide, key, ...sectionOpts },
            index
          ) =>
            (component || filters[remoteProp]) &&
            !hide && (
              <section key={index.toString()} {...sectionOpts}>
                {!shouldNotRenderHr.includes(key) && <hr />}
                <FilterTitle>{title}</FilterTitle>
                {component ||
                  renderToggleFilter(remoteProp, values || filters[remoteProp])}
              </section>
            )
        )}
      </form>
    )
  )
}

Filter.propTypes = {
  filter: PropTypes.shape({
    saleCategory: PropTypes.string,
    slug: PropTypes.string,
    locality: PropTypes.string,
    selectedLocalities: PropTypes.arrayOf(PropTypes.string),
    localities: PropTypes.arrayOf(PropTypes.shape({})),
    searchId: PropTypes.string,
    estatesList: PropTypes.arrayOf(PropTypes.string),
    valueRange: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    currentBannerList: PropTypes.arrayOf(PropTypes.string),
    campaigns: PropTypes.shape({
      campaign: PropTypes.bool.isRequired,
      secondCampaign: PropTypes.bool.isRequired,
    }).isRequired,
    visits: PropTypes.bool,
    desagio: PropTypes.number,
    noLegalActions: PropTypes.bool,
    noDebts: PropTypes.bool,
    surroundingsSearch: PropTypes.bool,
    filters: PropTypes.shape({}),
    apiFilters: PropTypes.oneOfType([PropTypes.any]),
    initialFilters: PropTypes.shape({}),
    page: PropTypes.number,
    orderBy: PropTypes.string,
    query: PropTypes.string,
    presetProfile: PropTypes.bool,
    hideBaseLayout: PropTypes.bool,
  }).isRequired,
  setField: PropTypes.func.isRequired,
  setCurrentBanner: PropTypes.func.isRequired,
  toggleFilter: PropTypes.func.isRequired,
  setClickedBy: PropTypes.func,
  blocked: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  clearFilters: PropTypes.func,
  hideSearchById: PropTypes.bool,
  setSelectedLocalities: PropTypes.func.isRequired,
  removeSelectedLocality: PropTypes.func.isRequired,
  specificFilters: PropTypes.arrayOf(PropTypes.string),
  setFieldManually: PropTypes.func.isRequired,
  toggleUniqueCheckbox: PropTypes.func,
  titleContainer: PropTypes.string,
  setCampaign: PropTypes.func.isRequired,
  setMaxValueRange: PropTypes.func.isRequired,
  setMinValueRange: PropTypes.func.isRequired,
}

Filter.defaultProps = {
  blocked: false,
  className: '',
  setClickedBy: () => {},
  type: 'page',
  clearFilters: () => {},
  hideSearchById: false,
  specificFilters: [],
  titleContainer: '',
  toggleUniqueCheckbox: () => {},
}

export default Filter
